import React from 'react';

const About = () => {
  return (
    <div className='ui container segment center aligned'>
      <h1>About the online HRAF Cross-Cultural Concordance</h1>

      <div className='text-left'>
        <div className='ui message'>
          <p>
            <b>Note:</b> This online concordance is still under development but
            it can be used for a considerable number of tasks. Please see the
            section at the end for remaining tasks.
          </p>
        </div>
        <p>
          The concordance is an online database providing information to “match”
          cases from each of 10 different cross-cultural samples. By a
          concordance or a match we mean that as far as we can determine the{' '}
          <b>
            data from two or more samples pertain to the same time and the same
            place focus for a particular society.
          </b>{' '}
          An important reason to match is to use data from more than one sample.
          Suppose a researcher wants to use some coded variables from the
          Standard Cross-Cultural Sample (SCCS) and wants to code new variables
          from eHRAF World Cultures. The variables for the SCCS are typically
          coded for a particular time and place based on the work of principal
          authorities (see White 1989), but eHRAF World Cultures usually has
          many more documents covering multiple time periods. Coding all
          variables for a society for the same time and place focus (in a
          synchronic comparison) is critically important for arriving at valid
          cross-cultural generalizations because mismatches make error more
          likely and lower correlations (Divale 1975; Ember et al. 1992; Ember
          and Ember 2009).
        </p>

        <p>
          Matching is not a simple task. For one thing, society or culture names
          can be quite different from sample to sample even when the same
          cultural group is discussed. In addition, sometimes the same name is
          used, but the time and place foci are sometimes different in different
          samples. For example, the Standard Cross-Cultural Sample’s Samoa focus
          is based on the work of the missionaries George Turner and John
          Stair’s for the “kingdom” of Aana in Western Upolu Island (1829 is the
          time focus), while the Ethnographic Atlas’s Samoa focus is based on
          the work of Margaret Mead and P. H. Buck in American Samoa in the
          1920s (Ember 2007). Seeing the name “Samoa” in two different samples
          by no means ensures a match!
        </p>

        <p>
          In this concordance, we have done the matching for you because each
          record is a time/place and culture focus record, not a record for the
          culture. That is why many eHRAF World Cultures records have multiple
          records because each record covers different time periods and
          different places. In general, time periods have been grouped together
          if they are within +/-10 years of each other. If we consider there to
          be a match across samples, you need only filter to two (or more)
          samples and you will only see the matching foci with accompanying
          metadata, such as the IDs, culture names, and dates for each sample
          that you have chosen for your filters.
        </p>

        <p>
          There are a considerable number of published cross-cultural samples.
        </p>

        <div className='ui ordered list'>
          <p>
            As part of the Human Relations Area Files’ collection of ethnography
            (1949 to the present) we include the following:
          </p>

          <div className='item'>
            Microfiche set (called HRAF Fiche in the concordance)*
          </div>
          <div className='item'>eHRAF World Cultures*</div>
          <div className='item'>
            Probability Sample Files (Naroll 1967; HRAF 1967; Lagacé 1979). Also
            called the HRAF Quality Control Sample.
          </div>
          <div className='item'>
            Simple Random Sample (selected by simple random sampling from this
            concordance with over 2,000 cultures)
          </div>
          <div className='item'>
            Standard Cross-Cultural Sample (Murdock and White 1969)**
          </div>
          <br />
          <p className='text-left'>
            Additional samples not included in the HRAF Collections:
          </p>
          <div className='item'>World Ethnographic Sample (Murdock 1957)</div>
          <div className='item'>
            Ethnographic Atlas (beginning in Ethnology 1 [ 1962: 113ff. and
            continuing in the Journal intermittently over succeeding years)
          </div>
          <div className='item'>Ethnographic Atlas Summary (Murdock 1967)</div>
          <div className='item'>
            Standard Ethnographic Sample, Second Edition (Naroll and Sipes 1973)
          </div>
        </div>

        <div>
          <p>
            <b>*</b> We do not recommend using either the whole microfiche set
            or eHRAF World Cultures as a sample because the sets include some
            collections added opportunistically (e.g., Immigrant Cultures in
            North America). Rather, we recommend that you consider
            representative samples within the HRAF collections; samples 3-5
            above were designed to be representative nonindustrial samples.
          </p>
          <p>
            <b>**</b> The Standard Cross-Cultural Sample was designed by Murdock
            and White (1969), but HRAF has now included all the SCCS cases in
            eHRAF World Cultures.
          </p>
        </div>

        <br />

        <div>
          <h2 className='text-center'>Tasks in process</h2>
          <div className='ui bulleted list'>
            <div className='item'>
              eHRAF World Cultures documents are currently matched to the SCCS
              and EA samples, but not all the eHRAF time/place foci have been
              entered for societies added since 1992.
            </div>
            <div className='item'>
              Language codes have not been updated since 1992; we suggest
              downloading language for the SCCS and EA cases from D-PLACE.org
              which is more up-to-date.
            </div>
            <div className='item'>
              Names need updating, including culture names, country names, and
              sometimes district names.
            </div>
          </div>
        </div>

        <br />

        <div>
          <h2 className='text-center'>Acknowledgements</h2>
          <p>
            This online concordance was partially supported by two grants from
            the National Science Foundation to the Human Relations Area Files:
            1) BCS-#2020156 (September 1, 2020-August 1, 2023) and 2)
            #BNS-8911173 (July 1, 1989-June 30, 1994). The Human Relations Area
            Files provided additional support.
          </p>

          <p>
            The initial computerized concordance was published in 1992 and
            appeared as Computerized Concordance of Cross-Cultural Samples
            (Ember et al. 1992). It was prepared with the special assistance of
            Hugh Page, Jr. as well as M. Marlene Martin and Timothy O’Leary.
          </p>

          <p>
            In the interim between 1992 and the present, Patricia Andreucci has
            helped Carol Ember provide matches for the societies added to eHRAF
            World Cultures.
          </p>

          <p>
            Sait Furkan Teke was mainly responsible for the development and
            production of the online concordance.
          </p>
        </div>

        <br />

        <div>
          <h2 className='text-center'>References</h2>
          <p>
            Divale, William T. 1975. Temporal Focus and Random Error in
            CrossCultural Hypothesis Tests. Behavior Science Research 10:19-36.
          </p>
          <p>
            Ember, Carol R. 2007. Using the HRAF Collection of Ethnography in
            Conjunction with the Standard Cross-Cultural Sample and the
            Ethnographic Atlas." Cross-Cultural Research 41, no. 4 (2007):
            396-427.
          </p>
          <p>
            Ember, Carol R. and Melvin Ember. 2009. Cross-Cultural Research
            Methods. 2nd edition. AltaMira.
          </p>
          <p>
            Ember, Carol R. with the assistance of Hugh Page, Jr., M. Marlene
            Martin and Timothy O’Leary. 1992. Computerized Concordance of
            Cross-Cultural Samples. New Haven: Human Relations Area Files.
          </p>
          <p>
            Ember, Carol R., Marc H. Ross, Michael Burton, and Candice Bradley.
            1991. Problems of Measurement in Cross-Cultural Research Using
            Secondary Data. Special issue of Behavior Science Research
            “Comparative and Cross-Cultural Research: Theory and Method”
            25:187-216.
          </p>
          <p>
            Human Relations Area Files. 1967. The HRAF Quality Control Sample
            Universe. Behavior Science Notes 2: 81-88.
          </p>
          <p>
            Lagacé, Robert. 1979. The HRAF Probability Sample: Retrospect and
            Prospect. Behavior Science Research 3: 211-229.
          </p>
          <p>
            Murdock, George P. 1957. World Ethnographic Sample. American
            Anthropologist 59: 664-687.
          </p>
          <p>
            Murdock, George P. 1967. Ethnographic Atlas: A Summary. Ethnology 6:
            109-236.
          </p>
          <p>
            Murdock, George P. and Douglas R. White. 1969. Standard
            Cross-Cultural Sample. Ethnology 8: 329-369. Reprinted in Herbert
            Barry III and Alice Schlegel, eds. 1980. Cross-Cultural Samples and
            Codes. Pittsburgh: University of Pittsburgh.
          </p>
          <p>
            Naroll, Raoul. 1967. The Proposed HRAF Probability Sample. Behavior
            Science Research 2: 70-80.
          </p>
          <p>
            Naroll, Raoul, and Richard G. Sipes. 1973. A Standard Ethnographic
            Sample: Second Edition. Current Anthropology 14: 111-142.
          </p>
          <p>
            White, Douglas R. Focused Ethnographic Bibliography: Standard
            Cross-Cultural Sample. Behavior Science Research 23, no. 1-4 (1989):
            1-145.
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;
